/**
 * Bundle Upsell Modal
 * Intercepts add-to-cart when single product (1x) is selected in bundle options.
 * Shows an upsell modal offering 2x / 3x bundles before adding to cart.
 *
 * Intercept strategy:
 *   1. Capture-phase click listener on submit button calls preventDefault()
 *      AND stopImmediatePropagation() — prevents the form submit event AND
 *      blocks any other click handlers (including third-party cart apps like
 *      UpCart that may intercept the button click).
 *   2. As a safety net we also set aria-disabled="true" so product-form.js's
 *      onSubmitHandler returns early if the submit event somehow still fires.
 *   3. A capture-phase submit listener on the form blocks submission while
 *      the modal is active.
 *   4. Only shows the modal when the cart is empty (0 items).
 */
(function () {
  'use strict';

  var modal = document.querySelector('.bundle-upsell-modal');
  if (!modal) return;

  // Escape the sticky product column's stacking context (z-index 2) so the modal layers above the pdp nav
  document.body.appendChild(modal);

  var sectionId = modal.dataset.sectionId;
  var submitButton = document.getElementById('ProductSubmitButton-' + sectionId);
  if (!submitButton) return;

  var closeBtn  = modal.querySelector('.bundle-upsell-modal__close');
  var acceptBtn = modal.querySelector('.bundle-upsell-modal__accept');
  var rejectBtn = modal.querySelector('.bundle-upsell-modal__reject');

  var bypassed = false;
  var modalActive = false;

  /* ── Helpers ───────────────────────────────────────────────── */

  function showModal() {
    updateModalPrices();
    modalActive = true;
    document.body.classList.add('overflow-hidden');
    modal.setAttribute('open', '');

    var radio2 = modal.querySelector('input[name="bundle-upsell"][value="2"]');
    if (radio2) radio2.checked = true;

    var firstFocusable = modal.querySelector('button, input');
    if (firstFocusable) firstFocusable.focus();
  }

  function hideModal() {
    modalActive = false;
    document.body.classList.remove('overflow-hidden');
    modal.removeAttribute('open');
    submitButton.removeAttribute('aria-disabled');
  }

  /**
   * Read prices from the (visually-hidden) PDP price elements and populate
   * the modal's price containers.
   */
  function updateModalPrices() {
    [2, 3].forEach(function (n) {
      var regularEl  = document.querySelector('.price__regular__' + n + ' span');
      var discountEl = document.querySelector('.price__discount__' + n + ' span');
      var target     = modal.querySelector('.bundle-upsell-modal__price--' + n);
      if (!target) return;

      var reg  = regularEl  ? regularEl.textContent.trim()  : '';
      var disc = discountEl ? discountEl.textContent.trim() : '';

      if (reg && disc) {
        target.innerHTML =
          '<span class="bundle-upsell-modal__price-regular">' + reg + '</span>' +
          ' <span class="bundle-upsell-modal__price-sale">' + disc + '</span>';
      } else if (disc) {
        target.innerHTML =
          '<span class="bundle-upsell-modal__price-sale">' + disc + '</span>';
      } else if (reg) {
        target.innerHTML =
          '<span class="bundle-upsell-modal__price-regular">' + reg + '</span>';
      }
    });
  }

  /**
   * Fetch cart data.
   * Returns a Promise that resolves to the cart object.
   */
  function fetchCart() {
    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .catch(function () { return { item_count: 0, attributes: {} }; });
  }

  /**
   * Write a cart attribute to track the upsell modal outcome.
   * Shows up on the order in Shopify Admin → "Additional details".
   */
  function setUpsellAttribute(value) {
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: { bundle_upsell: value } }),
      credentials: 'same-origin'
    }).catch(function () { /* swallow errors, proceed anyway */ });
  }

  /* ── Intercept (capture phase) ─────────────────────────────── */

  submitButton.addEventListener('click', function (evt) {
    if (bypassed) { bypassed = false; return; }

    var checkedBundle = document.querySelector('input[name="bundle"]:checked');
    if (!checkedBundle || checkedBundle.value !== '1') return;

    /* Block everything immediately — we'll decide async whether to
       show the modal or let the submission through. */
    evt.preventDefault();
    evt.stopImmediatePropagation();
    submitButton.setAttribute('aria-disabled', 'true');

    /* Only show the modal when the cart is empty */
    fetchCart().then(function (cart) {
      if (cart.item_count === 0) {
        showModal();
      } else {
        /* Cart is not empty → let the add-to-cart through.
           Only set the skip attribute if no upsell decision was recorded yet,
           so we don't overwrite an earlier accepted/rejected value. */
        var existing = (cart.attributes && cart.attributes.bundle_upsell) || '';
        var attrPromise = (!existing || existing === 'upsell_skipped_cart_not_empty')
          ? setUpsellAttribute('upsell_skipped_cart_not_empty')
          : Promise.resolve();
        attrPromise.then(function () {
          submitButton.removeAttribute('aria-disabled');
          bypassed = true;
          submitButton.click();
        });
      }
    });
  }, true);

  /**
   * Safety-net: also block form submit while modal is active.
   * This catches any programmatic form.submit() calls.
   */
  var form = submitButton.closest('form');
  if (form) {
    form.addEventListener('submit', function (evt) {
      if (modalActive) {
        evt.preventDefault();
        evt.stopImmediatePropagation();
      }
    }, true);
  }

  /* ── Accept ────────────────────────────────────────────────── */

  acceptBtn.addEventListener('click', function () {
    var selectedUpsell = modal.querySelector('input[name="bundle-upsell"]:checked');
    if (!selectedUpsell) return;

    var bundleValue = selectedUpsell.value;

    var pdpRadio = document.querySelector('.bundle__selector__' + bundleValue);
    if (pdpRadio) {
      pdpRadio.checked = true;
      pdpRadio.click();
    }

    hideModal();

    setUpsellAttribute('upsell_accepted_' + bundleValue + 'x').then(function () {
      submitButton.click();
    });
  });

  /* ── Reject ────────────────────────────────────────────────── */

  rejectBtn.addEventListener('click', function () {
    hideModal();
    bypassed = true;

    setUpsellAttribute('upsell_rejected').then(function () {
      submitButton.click();
    });
  });

  /* ── Close / Dismiss ───────────────────────────────────────── */

  closeBtn.addEventListener('click', function () {
    hideModal();
  });

  modal.addEventListener('click', function (evt) {
    if (evt.target === modal) hideModal();
  });

  document.addEventListener('keydown', function (evt) {
    if (evt.key === 'Escape' && modal.hasAttribute('open')) hideModal();
  });
})();
